import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import axios from 'axios';

// ============== Types ==============
export interface UploadItem {
  id: string;
  conversationId: number;
  fileName: string;
  fileSize: number;
  progress: number; // 0-100
  status: 'uploading' | 'processing' | 'completed' | 'error';
  phase: 'upload' | 'cdn'; // 'upload' = uploading to server, 'cdn' = server uploading to CDN
  error?: string;
  attachmentId?: number;
}

interface UploadContextType {
  uploads: UploadItem[];
  uploadFile: (conversationId: number, file: File) => Promise<number | null>;
  getConversationUploads: (conversationId: number) => UploadItem[];
  clearCompletedUploads: (conversationId: number) => void;
  cancelUpload: (uploadId: string) => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const useUpload = (): UploadContextType => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUpload must be used within an UploadProvider');
  }
  return context;
};

// ============== Provider ==============
interface UploadProviderProps {
  children: ReactNode;
}

// Track abort controllers for cancellation
const abortControllers: Map<string, AbortController> = new Map();

export const UploadProvider: React.FC<UploadProviderProps> = ({ children }) => {
  const [uploads, setUploads] = useState<UploadItem[]>([]);

  // Upload a file with progress tracking
  const uploadFile = useCallback(async (conversationId: number, file: File): Promise<number | null> => {
    const uploadId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const abortController = new AbortController();
    abortControllers.set(uploadId, abortController);

    // Add to uploads list
    const newUpload: UploadItem = {
      id: uploadId,
      conversationId,
      fileName: file.name,
      fileSize: file.size,
      progress: 0,
      status: 'uploading',
      phase: 'upload',
    };
    setUploads(prev => [...prev, newUpload]);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Auth is handled via HTTP-only cookies (withCredentials: true)
      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
      const response = await axios.post(
        `${baseUrl}/api/chat/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
          signal: abortController.signal,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;

            // When upload to server is complete, switch to "processing" phase
            // This is when the backend is uploading to CDN
            if (percentCompleted >= 100) {
              setUploads(prev => prev.map(u =>
                u.id === uploadId
                  ? { ...u, progress: 100, status: 'processing', phase: 'cdn' }
                  : u
              ));
            } else {
              setUploads(prev => prev.map(u =>
                u.id === uploadId
                  ? { ...u, progress: percentCompleted }
                  : u
              ));
            }
          },
        }
      );

      const attachmentId = response.data.id;

      // Mark as completed
      setUploads(prev => prev.map(u =>
        u.id === uploadId
          ? { ...u, status: 'completed', progress: 100, attachmentId }
          : u
      ));

      // Clean up abort controller
      abortControllers.delete(uploadId);

      // Auto-remove after 3 seconds
      setTimeout(() => {
        setUploads(prev => prev.filter(u => u.id !== uploadId));
      }, 3000);

      return attachmentId;
    } catch (error: any) {
      if (axios.isCancel(error)) {
        // Upload was cancelled
        setUploads(prev => prev.filter(u => u.id !== uploadId));
        return null;
      }

      const errorMessage = error.response?.data?.detail || error.message || 'Upload failed';

      // Mark as error
      setUploads(prev => prev.map(u =>
        u.id === uploadId
          ? { ...u, status: 'error', error: errorMessage }
          : u
      ));

      // Clean up abort controller
      abortControllers.delete(uploadId);

      throw error;
    }
  }, []);

  // Get uploads for a specific conversation
  const getConversationUploads = useCallback((conversationId: number): UploadItem[] => {
    return uploads.filter(u => u.conversationId === conversationId);
  }, [uploads]);

  // Clear completed uploads for a conversation
  const clearCompletedUploads = useCallback((conversationId: number) => {
    setUploads(prev => prev.filter(u =>
      u.conversationId !== conversationId || u.status === 'uploading'
    ));
  }, []);

  // Cancel an upload
  const cancelUpload = useCallback((uploadId: string) => {
    const controller = abortControllers.get(uploadId);
    if (controller) {
      controller.abort();
      abortControllers.delete(uploadId);
    }
    setUploads(prev => prev.filter(u => u.id !== uploadId));
  }, []);

  const value: UploadContextType = {
    uploads,
    uploadFile,
    getConversationUploads,
    clearCompletedUploads,
    cancelUpload,
  };

  return (
    <UploadContext.Provider value={value}>
      {children}
    </UploadContext.Provider>
  );
};

export default UploadContext;
